/**
 * Currency conversion and formatting utilities
 * for international withdrawal form
 */

/**
 * Calculate quote amount from base amount
 * Formula: (baseAmount × rate) - fee
 *
 * @param baseAmount - Amount to send in base currency
 * @param rate - Exchange rate (base to quote)
 * @param fee - Transaction fee in quote currency
 * @returns Calculated quote amount as string
 */
export function calculateQuoteAmount(
  baseAmount: string,
  rate: number,
  fee: number,
): string {
  if (!baseAmount || !rate) return "";

  const numericAmount = Number(baseAmount);
  if (isNaN(numericAmount) || numericAmount <= 0) return "";

  // Convert base to quote currency
  const grossAmount = numericAmount * rate;

  // Deduct fee from recipient amount
  const netAmount = Math.max(0, grossAmount - fee);

  // Round down to nearest whole number
  return Math.floor(netAmount).toString();
}

/**
 * Calculate base amount from desired quote amount
 * Formula: (quoteAmount + fee) / rate
 *
 * This is the reverse calculation - given a desired recipient amount,
 * calculate how much the sender needs to send
 *
 * @param quoteAmount - Desired amount recipient should receive
 * @param rate - Exchange rate (base to quote)
 * @param fee - Transaction fee in quote currency
 * @returns Calculated base amount as string
 */
export function calculateBaseAmount(
  quoteAmount: string,
  rate: number,
  fee: number,
): string {
  if (!quoteAmount || !rate) return "";

  const numericAmount = Number(quoteAmount);
  if (isNaN(numericAmount) || numericAmount <= 0) return "";

  // Add fee back to get gross amount
  const grossAmount = numericAmount + fee;

  // Convert quote to base currency
  const baseAmount = grossAmount / rate;

  // Round up to ensure recipient gets at least the desired amount
  return Math.ceil(baseAmount).toString();
}

/**
 * Format currency value with thousand separators
 *
 * @param value - Raw numeric string (no separators)
 * @returns Formatted string with commas (e.g., "10,000")
 */
export function formatCurrencyInput(value: string): string {
  // Remove all non-digit characters
  const cleaned = value.replace(/[^\d]/g, "");

  if (!cleaned) return "";

  // Convert to number and format with thousand separators
  const number = Number(cleaned);

  if (isNaN(number)) return "";

  return number.toLocaleString("en-US");
}

/**
 * Parse formatted currency back to raw number string
 * Removes all formatting characters (commas, spaces, etc.)
 *
 * @param formatted - Formatted currency string (e.g., "10,000")
 * @returns Raw numeric string (e.g., "10000")
 */
export function parseCurrencyInput(formatted: string): string {
  return formatted.replace(/[^\d]/g, "");
}

/**
 * Validate if a string represents a valid numeric amount
 *
 * @param value - String to validate
 * @returns True if valid number > 0
 */
export function isValidAmount(value: string): boolean {
  if (!value) return false;

  const cleaned = parseCurrencyInput(value);
  const number = Number(cleaned);

  return !isNaN(number) && number > 0;
}

/**
 * Calculate the gross amount before fee deduction
 * Useful for showing breakdown: "baseAmount × rate = grossAmount"
 *
 * @param baseAmount - Amount to send
 * @param rate - Exchange rate
 * @returns Gross amount before fee
 */
export function calculateGrossAmount(baseAmount: string, rate: number): number {
  if (!baseAmount || !rate) return 0;

  const numericAmount = Number(baseAmount);
  if (isNaN(numericAmount) || numericAmount <= 0) return 0;

  return Math.floor(numericAmount * rate);
}
